import type { Rating } from "@homeinstead/schemas";
import type { CSSProperties } from "react";
import type { StepItem } from "../StepInputRenderer";

import { cn, formatNumber } from "@homeinstead/ui";
import { useId, useLayoutEffect, useRef, useState } from "react";
import { isDisplayableRating } from "~www/lib/office/get-aggregate-rating";
import "./button-stack-motion.css";

type ReviewStarsStepItem = Extract<StepItem, { _type: "reviewStars" }>;

type ButtonStackRatingStripProps = {
  input: ReviewStarsStepItem;
  officeRating?: Omit<Rating, "_type">;
};

const DEFAULT_ARIA_LABEL = "Rating: {{reviewStarCount}} out of 5";

const DEFAULT_REVIEW_TEXT =
  "{{reviewStarCount}} rating on {{reviewPlatform}} ({{roundedReviewCount}})";

const STAR_SLOTS = ["first", "second", "third", "fourth", "fifth"] as const;

type RatingStripPin = {
  "--button-stack-rating-left": string;
  "--button-stack-rating-right": string;
  "--button-stack-rating-width": string;
};

/**
 * CSSType has no index signature for custom properties. The cast stays at
 * this boundary so star index and clip can be set without a Tailwind class
 * per value.
 */
function toCustomPropertyStyle(properties: {
  [key: `--${string}`]: number | string;
}): CSSProperties {
  return properties as CSSProperties;
}

/**
 * Sticky cannot climb out of the step wrapper's overflow clipping, so the
 * strip is fixed while the hero extends past the viewport. These variables
 * describe the section box. The `md:` classes on the strip choose the edge,
 * so the pin uses the same Tailwind screen as the layout.
 */
function readRatingStripPin(section: HTMLElement): RatingStripPin | null {
  const rect = section.getBoundingClientRect();
  if (rect.bottom <= window.innerHeight) {
    return null;
  }

  return {
    "--button-stack-rating-left": `${Math.round(rect.left)}px`,
    "--button-stack-rating-right": `${Math.round(window.innerWidth - rect.right)}px`,
    "--button-stack-rating-width": `${Math.round(rect.width)}px`,
  };
}

function isSameRatingStripPin(
  current: RatingStripPin | null,
  next: RatingStripPin | null,
) {
  if (current === next) {
    return true;
  }

  if (!current || !next) {
    return false;
  }

  return (
    current["--button-stack-rating-left"] ===
      next["--button-stack-rating-left"] &&
    current["--button-stack-rating-right"] ===
      next["--button-stack-rating-right"] &&
    current["--button-stack-rating-width"] ===
      next["--button-stack-rating-width"]
  );
}

function roundToNearestTenth(value: number) {
  return Math.round(value * 10) / 10;
}

/**
 * Shared RatingStars rounds a 0.8 fraction up to another full star. This
 * strip keeps that fraction so the entrance can wipe a partial star after
 * the full ones.
 */
function splitButtonStackRating(rating: number) {
  const rounded = Math.min(5, Math.max(0, roundToNearestTenth(rating)));
  const fullStars = Math.floor(rounded);
  const remainder = roundToNearestTenth(rounded - fullStars);
  const hasPartial = remainder > 0 && fullStars < 5;

  return {
    fullStars,
    partialFill: hasPartial ? remainder : 0,
  };
}

function resolveReviewText(
  template: string,
  values: {
    reviewPlatform: string;
    reviewStarCount: number;
    roundedReviewCount: string;
  },
) {
  return template
    .replaceAll("{{reviewStarCount}}", String(values.reviewStarCount))
    .replaceAll("{{reviewPlatform}}", values.reviewPlatform)
    .replaceAll("{{roundedReviewCount}}", values.roundedReviewCount);
}

const STAR_PATH =
  "M7.50779 0.330193C7.59595 0.128911 7.78872 0 7.99991 0C8.21109 0 8.40386 0.128906 8.49203 0.330193L10.4868 4.86601C10.5421 4.99216 10.6566 5.07901 10.7882 5.09487L15.5213 5.68081C15.7311 5.707 15.9081 5.85865 15.9732 6.06959C16.039 6.28052 15.9811 6.51145 15.8259 6.66242L12.3252 10.0519C12.2278 10.1463 12.1837 10.2869 12.2094 10.4234L13.1403 15.3205C13.1817 15.5383 13.0989 15.761 12.9271 15.8913C12.7561 16.0216 12.5284 16.036 12.3436 15.9278L8.18618 13.4862C8.07039 13.4186 7.92961 13.4186 7.81381 13.4862L3.65574 15.9271C3.47086 16.0353 3.24324 16.0215 3.07219 15.8906C2.90048 15.7603 2.81758 15.5376 2.85903 15.3198L3.78996 10.4227C3.81562 10.2862 3.7722 10.1456 3.67417 10.0512L0.174134 6.66242C0.0188663 6.51215 -0.0390247 6.28052 0.026762 6.06959C0.0918953 5.85865 0.268873 5.70631 0.478747 5.68081L5.21182 5.09487C5.3434 5.07833 5.45787 4.99216 5.51315 4.86601L7.50779 0.330193Z";

function ButtonStackStar({
  filled,
  gradientId,
}: {
  filled: boolean;
  gradientId: string;
}) {
  return (
    <svg
      aria-hidden="true"
      className={cn("size-4", !filled && "opacity-25")}
      fill="none"
      height={16}
      viewBox="0 0 16 16"
      width={16}
    >
      <path d={STAR_PATH} fill={`url(#${gradientId})`} />
    </svg>
  );
}

function ButtonStackRatingStrip({
  input,
  officeRating,
}: ButtonStackRatingStripProps) {
  const gradientId = `button-stack-star-${useId().replaceAll(":", "")}`;
  const panelRef = useRef<HTMLDivElement>(null);
  const [pin, setPin] = useState<RatingStripPin | null>(null);
  const platform = input.reviewPlatform ?? "Google";

  // The strip's resting place is the bottom of the hero. Sticky cannot pull
  // it up from below the fold, and the step wrapper clips overflow, so while
  // the hero extends past the viewport the strip is fixed to the hero's box.
  // Once the hero bottom reaches that edge, it returns to the hero and
  // scrolls away with it.
  useLayoutEffect(() => {
    const section = panelRef.current?.closest("section");
    if (!section) {
      return;
    }

    const update = () => {
      const next = readRatingStripPin(section);
      setPin((current) =>
        isSameRatingStripPin(current, next) ? current : next,
      );
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(update);
    observer?.observe(section);

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, []);
  let rating: number;
  let reviewCount: number;

  if (input.source === "manual") {
    if (!input.manualRating || !input.manualReviewCount) {
      return null;
    }

    rating = input.manualRating;
    reviewCount = input.manualReviewCount;
  } else if (!isDisplayableRating(officeRating)) {
    return null;
  } else {
    rating = officeRating.rating;
    reviewCount = officeRating.ratingCount;
  }

  const placeholders = {
    reviewPlatform: platform,
    reviewStarCount: rating,
    // The rating bar renders the count suffix in lowercase.
    roundedReviewCount: formatNumber(reviewCount).replace(/[KMB]$/, (suffix) =>
      suffix.toLowerCase(),
    ),
  };
  const label = resolveReviewText(
    input.reviewText ?? DEFAULT_REVIEW_TEXT,
    placeholders,
  );
  const resolvedAriaLabel = resolveReviewText(
    input.ariaLabel ?? DEFAULT_ARIA_LABEL,
    placeholders,
  );
  const { fullStars, partialFill } = splitButtonStackRating(rating);

  return (
    <div
      className={cn(
        // Below md the strip stays short: 16px inline padding and 4px block
        // padding. The roomier 16px desktop padding lives on the unpinned
        // branch so a pinned bar can still clear the home indicator. The
        // section box is on custom properties so these `md:` utilities stay
        // the breakpoint.
        "button-stack-rating-panel bottom-0 z-20 flex items-center justify-end bg-black/50 px-4 py-1 font-jakarta font-medium text-base text-white",
        pin
          ? "fixed right-auto left-[var(--button-stack-rating-left)] w-[var(--button-stack-rating-width)] pt-1 pb-[max(0.25rem,env(safe-area-inset-bottom))] md:right-[var(--button-stack-rating-right)] md:left-auto md:w-max md:pt-4 md:pb-[max(1rem,env(safe-area-inset-bottom))]"
          : "absolute inset-x-0 md:inset-x-auto md:right-0 md:w-max md:p-4",
      )}
      ref={panelRef}
      style={pin ? toCustomPropertyStyle(pin) : undefined}
    >
      <svg aria-hidden="true" className="absolute size-0" focusable="false">
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id={gradientId}
            x1="8"
            x2="8"
            y1="0"
            y2="16"
          >
            <stop offset="0.25" stopColor="white" />
            <stop offset="0.754808" stopColor="#FAEE6E" />
          </linearGradient>
        </defs>
      </svg>
      <div
        aria-label={resolvedAriaLabel}
        className="button-stack-rating-row flex w-fit flex-row items-center gap-3"
        role="img"
      >
        <span className="flex items-center gap-1">
          {STAR_SLOTS.slice(0, fullStars).map((slot, index) => (
            <span
              className="button-stack-star-pop inline-flex"
              data-testid="button-stack-full-star"
              key={slot}
              style={toCustomPropertyStyle({
                "--button-stack-star-index": index,
              })}
            >
              <ButtonStackStar filled gradientId={gradientId} />
            </span>
          ))}
          {partialFill > 0 ? (
            <span
              className="relative inline-flex size-4"
              data-testid="button-stack-partial-star"
            >
              <ButtonStackStar filled={false} gradientId={gradientId} />
              <span
                className="button-stack-partial-star button-stack-star-clip absolute inset-0"
                style={toCustomPropertyStyle({
                  "--button-stack-clip-right": `${Math.round((1 - partialFill) * 100)}%`,
                  "--button-stack-star-index": fullStars,
                })}
              >
                <ButtonStackStar filled gradientId={gradientId} />
              </span>
            </span>
          ) : null}
          {STAR_SLOTS.slice(fullStars + (partialFill > 0 ? 1 : 0)).map(
            (slot) => (
              <span
                className="inline-flex"
                data-testid="button-stack-empty-star"
                key={slot}
              >
                <ButtonStackStar filled={false} gradientId={gradientId} />
              </span>
            ),
          )}
        </span>
        <span className="button-stack-rating-label inline-block whitespace-nowrap leading-8">
          {label}
        </span>
      </div>
    </div>
  );
}

export { ButtonStackRatingStrip };
